import React, { FC, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Alert, Button, Dropdown, Empty, Menu, Modal, Pagination, Spin } from 'antd';
import { FileTextOutlined, MoreOutlined } from '@ant-design/icons';
import {
	complianceRequestInstancesRestCrudThunks,
	approveComplianceRequestInstance,
} from '../../thunks/compliance_request_instances_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { withUserType } from '../withUserType';
import {
	COMPLIANCE_INSTANCE_STATUSES,
	ComplianceRequest,
	ComplianceRequestInstance,
} from '../../types/ComplianceTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ComplianceStatusDisplay } from '../compliance_common/ComplianceStatusDisplay';
import { ComplianceExpiryDisplay } from '../compliance_common/ComplianceExpiryDisplay';
import RejectComplianceInstanceModal from './RejectComplianceInstanceModal';
import useMountEffect from '../../hooks/useMountEffect';

const DETAIL_TARGET_COLLECTION = 'complianceRequestDetailInstances';
const DETAIL_PAGE_SIZE = 10;

interface ComplianceRequestDetailModalProps {
	visible: boolean;
	// Falls back to the logged-in user's role (session.userType, injected by
	// withUserType) when not explicitly provided.
	roleType?: string;
	complianceRequest: ComplianceRequest;
	onCancel: () => void;
	instances: ComplianceRequestInstance[];
	fetching: boolean;
	fetchErrors: string[];
	total: number;
	fetchInstances: (complianceRequestId: number, pagination: any) => Promise<any>;
	approveInstance: (id: number) => Promise<any>;
}

const ComplianceRequestDetailModal: FC<ComplianceRequestDetailModalProps> = ({
	visible,
	roleType,
	userType,
	complianceRequest,
	onCancel,
	instances,
	fetching,
	fetchErrors,
	total,
	fetchInstances,
	approveInstance,
}): React.ReactElement => {
	const role = roleType || userType;
	const [rejectingInstance, setRejectingInstance] = useState(null);
	const [detailPage, setDetailPage] = useState(1);

	useMountEffect(() => {
		fetchInstances(complianceRequest.id, { current: 1, pageSize: DETAIL_PAGE_SIZE });
	});

	const refresh = () =>
		fetchInstances(complianceRequest.id, { current: detailPage, pageSize: DETAIL_PAGE_SIZE });

	const handlePageChange = (page) => {
		setDetailPage(page);
		fetchInstances(complianceRequest.id, { current: page, pageSize: DETAIL_PAGE_SIZE });
	};

	const handleApprove = (instance) => {
		approveInstance(instance.id).then(refresh);
	};

	const openRejectModal = (instance) => setRejectingInstance(instance);
	const closeRejectModal = () => setRejectingInstance(null);

	const handleRejected = () => {
		setRejectingInstance(null);
		refresh();
	};

	const renderRowActions = (instance) => {
		const canReview = instance.status === COMPLIANCE_INSTANCE_STATUSES.PENDING_REVIEW;
		const handleApproveClick = () => handleApprove(instance);
		const handleRejectClick = () => openRejectModal(instance);
		const menu = (
			<Menu>
				<Menu.Item key="approve" disabled={!canReview} onClick={handleApproveClick}>
					Approve
				</Menu.Item>
				<Menu.Item key="reject" disabled={!canReview} danger={true} onClick={handleRejectClick}>
					Reject
				</Menu.Item>
			</Menu>
		);
		return (
			<Dropdown overlay={menu} trigger={['click']}>
				<Button icon={<MoreOutlined />} />
			</Dropdown>
		);
	};

	const renderInstanceRow = (instance: ComplianceRequestInstance) => {
		const fileUrl = nullSafeGet('linkedComplianceDocument.fileUrl', instance);
		const dueBy = nullSafeGet('complianceRequest.dueBy', instance);
		return (
			<div
				key={instance.id}
				className="grid grid-cols-12 items-center gap-3 border-0 border-b border-solid border-grey-4 px-4 py-3"
			>
				<div className="bodymedium col-span-3">
					{nullSafeGet('supplierFacilityName', instance) ||
						nullSafeGetOrElse('supplierFacility.name', instance, '--')}
				</div>
				<div className="col-span-3">
					<div className="flex flex-col gap-1">
						<ComplianceStatusDisplay status={instance.status} perspective="buyer" />
						{dueBy ? (
							<span className="footnoteregular text-character-secondary">
								Due: {moment(dueBy).format('D MMM, YYYY')}
							</span>
						) : null}
					</div>
				</div>
				<div className="col-span-2">
					<ComplianceExpiryDisplay
						expiryStatus={instance.expiryStatus}
						expiryDate={nullSafeGet('linkedComplianceDocument.expiryDate', instance)}
					/>
				</div>
				<div className="col-span-3">
					{fileUrl ? (
						<Button
							icon={<FileTextOutlined />}
							href={fileUrl}
							target="_blank"
							rel="noopener noreferrer"
						>
							VIEW DOCUMENT
						</Button>
					) : (
						<span className="text-character-secondary">--</span>
					)}
				</div>
				<div className="col-span-1 flex justify-end">{renderRowActions(instance)}</div>
			</div>
		);
	};

	const renderBody = () => {
		if (fetching && instances.length === 0) {
			return (
				<div className="flex justify-center py-12">
					<Spin />
				</div>
			);
		}
		if (fetchErrors.length > 0) {
			return (
				<Alert
					type="error"
					message="Couldn't load supplier submissions."
					description={fetchErrors.join(', ')}
				/>
			);
		}
		if (instances.length === 0) {
			return (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description="No suppliers have been targeted by this request yet."
				/>
			);
		}
		return (
			<div>
				<div className="footnoteMedium grid grid-cols-12 gap-3 border-0 border-b border-solid border-grey-4 bg-grey-1 px-4 py-3 text-character-secondary">
					<div className="col-span-3">Supplier ({total})</div>
					<div className="col-span-3">Approval status</div>
					<div className="col-span-2">Expiration Date</div>
					<div className="col-span-3">Document</div>
					<div className="col-span-1" />
				</div>
				{instances.map(renderInstanceRow)}
				{total > DETAIL_PAGE_SIZE ? (
					<div className="flex flex-row justify-end px-4 py-3">
						<Pagination
							current={detailPage}
							pageSize={DETAIL_PAGE_SIZE}
							total={total}
							showSizeChanger={false}
							onChange={handlePageChange}
						/>
					</div>
				) : null}
			</div>
		);
	};

	const title = (
		<span>
			{complianceRequest.name}
			{complianceRequest.sampleFileUrl ? (
				<a
					className="footnoteregular ml-3"
					href={complianceRequest.sampleFileUrl}
					target="_blank"
					rel="noopener noreferrer"
				>
					Sample
				</a>
			) : null}
		</span>
	);

	const footer = (
		<Button type="primary" onClick={onCancel}>
			DONE
		</Button>
	);

	return (
		<Modal
			visible={visible}
			title={title}
			onCancel={onCancel}
			footer={footer}
			width={960}
			bodyStyle={{ padding: 0 }}
		>
			{renderBody()}
			{rejectingInstance && (
				<RejectComplianceInstanceModal
					visible={!!rejectingInstance}
					roleType={role}
					instance={rejectingInstance}
					onCancel={closeRejectModal}
					onSuccess={handleRejected}
				/>
			)}
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	instances: getRecordsForTargetCollection(
		state.compliance_request_instances,
		DETAIL_TARGET_COLLECTION
	),
	// Read the per-collection flags, not the slice root — the root flags are mutated
	// by every compliance_request_instances read (e.g. the Overview's 500-row fetch),
	// which would otherwise spin/error this modal on unrelated fetches.
	fetching: nullSafeGetOrElse(
		`${DETAIL_TARGET_COLLECTION}.fetching`,
		state.compliance_request_instances,
		false
	),
	fetchErrors: nullSafeGetOrElse(
		`${DETAIL_TARGET_COLLECTION}.fetchErrors`,
		state.compliance_request_instances,
		[]
	),
	total: nullSafeGetOrElse(
		`${DETAIL_TARGET_COLLECTION}.pagination.total`,
		state.compliance_request_instances,
		0
	),
});

const mapDispatchToProps = (dispatch, ownProps) => {
	const role = ownProps.roleType || ownProps.userType;
	return {
		fetchInstances: (complianceRequestId, pagination) =>
			dispatch(
				complianceRequestInstancesRestCrudThunks(role).read(
					{ complianceRequestId },
					DETAIL_TARGET_COLLECTION,
					pagination
				)
			),
		approveInstance: (id) => dispatch(approveComplianceRequestInstance(role)(id)),
	};
};

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(ComplianceRequestDetailModal)
);
